<template>
  <div class="comment-main">
    <error-view v-if="hasError" module="message"/>
    <template v-else>
      <van-nav-bar
        class="file-header nav-bar-bgcolor"
        :title="$t('file.action.comment')"
        :left-text="showLeft ? $t('base.close') : ''"
        @click-left="handleClose"
      >
        <i v-if="showItem && showLeft" slot="left" class="iconfont icon-back"/>
      </van-nav-bar>
      <div v-if="showItem && $route.params.module === 'message' && commentItem" class="comment-target" @click="handleCommentItemClick">
        <div class="thumb-icon">
          <thumb-icon
            :type="commentItem.type"
            :name="commentItem.name"
            :thumbnail="commentItem.thumbnail"
            :space="commentItem.space"
            :typeIcon="commentItem.type_icon"
          />
        </div>
        <div class="main-info">
          <div class="file-name two-line-ellipsis">{{commentItem.name}}</div>
          <div class="size">
            {{commentItem.action_time + $t('base.comma') + commentItem.approx_size}}
          </div>
          <div class="parent-info" v-show="isFile">
            {{$t('file.whereIn')}}
            <a href="javascript:;" @click.stop="goToParent">{{parent.name}}</a>
          </div>
        </div>
        <div class="action">
          <i class="iconfont icon-arrow-next"></i>
        </div>
      </div>

      <ul :class="['comment-list', (!comments.length || isFetching) && 'content-center']" ref="listWrapper">
        <van-loading v-if="isFetching"/>
        <template v-else-if="!comments.length">
          <svg-icon v-if="prem_comment" icon="comment-empty"/>
          <svg-icon v-else icon="empty-nopermission"/>
          <p v-if="prem_comment">{{$t('file.comment.empty')}}</p>
          <p v-else>{{$t('file.comment.empty.noPrem')}}</p>
        </template>
        <li v-for="item of comments" :key="item.id">
          <comment-item
            :item="item"
            :unreadIndex="unreadList.indexOf(item.child_message_id)"
            @play-audio="voicePlay"
            @reply="handleReply"
            @comment-delete="handleRemoveComment"
          />
        </li>
      </ul>

      <comment-form
        v-if="!hasError && show && prem_comment"
        :isFetching="isFetching"
        :replyData="replyData"
        :showAt="showAt"
        @add-comment="handleAddComment"
      />
    </template>
  </div>
</template>
<script>
import ajax from '@/utils/ajax'
import {i18n} from '@/utils/i18n'
import {FILE_INFO, COMMENTS_INFO, CHILD_MESSAGES_MARK_READ} from '@/config/api'
import {normalizeFile, getParentItem} from '@/utils/fileUtils'
import CommentItem from './CommentItem'
import CommentForm from './CommentForm'
import ErrorView from '../utils/ErrorView'
import {ua} from '@/utils/common'
import config from '@/config'

export default {
  name: 'CommentView',
  i18n,
  components: {
    CommentItem,
    CommentForm,
    ErrorView
  },
  props: {
    show: Boolean,
    showItem: Boolean
  },
  data () {
    return {
      item_type: null,
      commentItem: null,
      isFetching: true,
      pageCount: 1,
      comments: [],
      playingAudio: {},
      replyData: {
        index: 0,
        item: null
      },
      unreadList: [],
      hasError: false,
      showLeft: false
    }
  },
  computed: {
    showAt () {
      if (config.university_account_status) {
        return false
      }
      if (!this.commentItem) {
        return false
      }
      if (this.commentItem.type === 'file') {
        return this.commentItem.is_collab_folder
      } else if (this.commentItem.type === 'folder') {
        return this.commentItem.is_invited_collab_folder || this.commentItem.is_owned_collab_folder || this.commentItem.is_external_collab_folder
      }
      return false
    },
    isFile () {
      return this.item_type === 'file'
    },
    parent () {
      return getParentItem(this.commentItem)
    },
    prem_comment () {
      return !this.commentItem || (this.commentItem && this.commentItem.action_permissions && this.commentItem.action_permissions.includes('perm_comment'))
    }
  },
  watch: {
    show (show) {
      if (show) {
        this.commentItem = null
        this.comments = []
        this.hasError = false
        this.item_type = this.commentItemType()
        this.fetchCommentItem()
      } else {
        this.stopVoicePlay()
      }
    },
    'store.actionState' (state) {
      const { action, id, name, comment, commentId } = state
      if (!this.commentItem || this.commentItem.id !== id) return

      switch (action) {
        case 'rename':
          this.commentItem.name = name
          break

        case 'delete':
          this.hasError = true
          break

        case 'addComment':
          this.comments.push(comment)
          break

        case 'removeComment':
          this.comments = this.comments.filter(item => item.id !== commentId)
          break

        default:
          break
      }
    },
    'store.navStatus' (navStatus) {
      // 新建文件夹并进入
      if (navStatus === 0) {
        this.showLeft = true
      }
    }
  },
  created () {
    this.item_type = this.commentItemType()
    this.fetchCommentItem()
    this.markRead()
  },
  mounted () {
    if (!ua.fengyun) {
      this.showLeft = true
    } else if (ua.fengyun && this.store.navStatus === 0) {
      this.showLeft = true
    }
  },
  updated () {
    const listWrapper = this.$refs.listWrapper
    if (!listWrapper) return
    const anchor = listWrapper.querySelector('.comment-list #unread-0')
    const {message_id} = this.$route.query
    if (message_id && anchor) {
      setTimeout(() => {
        listWrapper.scrollTop = anchor.offsetTop - 38
      })
    } else {
      setTimeout(() => {
        const lastComment = listWrapper.querySelector('li:last-child')
        listWrapper.scrollTop = lastComment ? lastComment.offsetTop : listWrapper.scrollHeight
      })
    }
  },
  beforeDestroy () {
    clearTimeout(this.timer)
    this.stopVoicePlay()
  },
  methods: {
    commentItemId () {
      const {typed_id} = this.$route.query
      return typed_id && typed_id.split('_')[1]
    },
    commentItemType () {
      const {typed_id} = this.$route.query
      return typed_id && typed_id.split('_')[0]
    },
    fetchCommentItem () {
      this.isFetching = true
      const {typed_id} = this.$route.query
      ajax({
        url: `${FILE_INFO}?item_typed_id=${typed_id}`,
        accept_all_errors: true
      }).then(res => {
        if (res && res.success && res.item) {
          normalizeFile(res.item)
          this.commentItem = res.item
          this.hasError = false
          this.fetchCommentList()
        } else if (res && res.errors) {
          this.hasError = true
          this.isFetching = false
        }
      })
    },
    fetchCommentList () {
      const { module, id } = this.$route.params
      const {typed_id} = this.$route.query
      let url = `${COMMENTS_INFO}?item_typed_id=${typed_id}&no_pagination=1`
      if (module === 'message' && id) {
        url += `&message_id=${id}`
      }
      ajax({
        url,
        accept_all_errors: true
      }).then(res => {
        if (res && res.success) {
          this.getUnreadList(res.comments)
          this.comments = res.comments
          this.hasError = false
        } else if (res && res.errors && this.prem_comment) {
          this.hasError = true
        }
        this.isFetching = false
      })
    },
    voicePlay (audio) {
      if (audio.src === this.playingAudio.src) {
        if (audio.paused || audio.ended) {
          audio.pause()
          audio.play()
        } else {
          audio.pause()
        }
      } else {
        this.stopVoicePlay()
        audio.play()
        this.playingAudio = audio
      }
    },
    stopVoicePlay () {
      if (this.playingAudio.src) {
        this.playingAudio.currentTime = 0
        this.playingAudio.pause()
      }
    },
    handleReply (item) {
      this.replyData = {
        index: (this.replyData.index + 1) % 2,
        item
      }
    },
    getUnreadList (comments) {
      // 消息点进评论 才会有unread字段
      let unreadList = []
      comments.forEach(comment => {
        if (comment.unread) {
          comment.child_message_id && unreadList.push(comment.child_message_id)
        }
      })

      this.unreadList = unreadList

      if (unreadList.length) {
        this.markRead(unreadList)
      }
    },
    markRead (unreadList) {
      if (unreadList && unreadList.length) {
        ajax({
          url: CHILD_MESSAGES_MARK_READ,
          data: {
            child_message_ids: unreadList,
            type: 'comment'
          }
        }).then(res => {
          if (res && res.success) {
            unreadList.forEach(child_message_id => {
              let unreadMessage = this.comments.find(item => {
                return item.child_message_id && item.child_message_id === child_message_id
              })
              Object.assign(unreadMessage, {unread: 0})
            })
          }
          this.setState({actionState: {
            action: 'markRead',
            id: this.$route.params.id
          }})
        })
      } else {
        this.timer = setTimeout(() => {
          const messageId = this.$route.params.id
          messageId && this.setState({actionState: {
            action: 'markRead',
            id: messageId
          }})
        }, 300)
      }
    },
    handleAddComment (comment) {
      this.setState({actionState: {
        action: 'addComment',
        id: this.commentItem.id,
        comment
      }})
    },
    handleRemoveComment (id) {
      this.setState({actionState: {
        action: 'removeComment',
        id: this.commentItem.id,
        commentId: id
      }})
    },
    handleCommentItemClick () {
      if (this.isFile) {
        !this.itemDeleted && this.$emit('item-preview', this.commentItem)
      } else if (this.commentItemType() === 'folder') {
        this.$router.push(`/files/folder/${this.commentItemId()}`)
      }
    },
    goToParent () {
      this.$router.push(`/files/${this.parent.space}/${this.parent.id || ''}`)
    },
    handleClose () {
      this.$emit('close-comment')
    }
  }
}
</script>
<style lang="postcss">
@import 'variables';

.comment-main {
  display: flex;
  flex-direction: column;
  height: 100%;

  &>* {
    flex: 0 0 auto;
  }

  & .comment-target {
    display: flex;
    box-sizing: border-box;
    min-height: 50px;
    padding: 15px 0;
    background-color: #fff;
    margin-bottom: 10px;
  }

  & .comment-target {
    & .thumb-icon {
      flex: 0 0 auto;
      width: 55px;

      & img,
      & svg {
        display: block;
        width: 30px;
        height: 30px;
        margin: 6px auto 0;
      }
    }
  }

  & .main-info {
    flex: 1 1 auto;
    overflow: hidden;

    & .file-name {
      font-size: 16px;
      color: #000;
    }

    & .size {
      font-size: 12px;
      color: var(--secondlyText);
    }
  }

  & .action {
    flex: 0 0 auto;
    width: 50px;
    height: 40px;
    line-height: 40px;
    text-align: center;
    font-size: 16px;
    color: var(--secondlyText);
  }

  & .parent-info {
    margin-top: 10px;
    font-size: 14px;
  }

  & .comment-list {
    flex: 1 1 auto;
    overflow: auto;

    & li:last-of-type {
      & .comment-info {
        border-bottom: 0
      }
    }

    & .icon-prompt {
      font-size: 56px;
      color: #b2b7ca
    }
  }
}
</style>
